import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ROUTES } from '../../../shared/config/routes';
import { useAuth } from '../../auth/context/AuthContext';

const cards = [
  {
    title: 'Clientes',
    description: 'Consulta registros de tutores y menores a cargo.',
    to: ROUTES.adminCustomers,
    icon: '📋',
    gradient: 'from-blue-500 to-indigo-500',
    shadow: 'shadow-blue-200/50',
    roles: ['ADMIN', 'STAFF'],
  },
  {
    title: 'Bonos',
    description: 'Listado completo de bonos con filtros y detalle.',
    to: ROUTES.adminVoucherList,
    icon: '🎟️',
    gradient: 'from-orange-500 to-amber-500',
    shadow: 'shadow-orange-200/50',
    roles: ['ADMIN', 'STAFF'],
  },
  {
    title: 'Crear bono',
    description: 'Venta rápida de un bono nuevo sin registro previo.',
    to: ROUTES.adminVoucherCreate,
    icon: '➕',
    gradient: 'from-emerald-500 to-teal-500',
    shadow: 'shadow-emerald-200/50',
    roles: ['ADMIN'],
  },
  {
    title: 'Canjear bono',
    description: 'Buscar persona, identificar bono y descontar horas.',
    to: ROUTES.adminVoucherRedeem,
    icon: '🔄',
    gradient: 'from-violet-500 to-purple-500',
    shadow: 'shadow-violet-200/50',
    roles: ['ADMIN', 'STAFF'],
  },
  {
    title: 'Usuarios',
    description: 'Crea y gestiona cuentas de empleados.',
    to: ROUTES.adminUsers,
    icon: '👥',
    gradient: 'from-rose-500 to-pink-500',
    shadow: 'shadow-rose-200/50',
    roles: ['ADMIN'],
  },
  {
    title: 'Mi perfil',
    description: 'Cambia tu avatar, nombre o contraseña.',
    to: ROUTES.adminProfile,
    icon: '⚙️',
    gradient: 'from-slate-500 to-slate-700',
    shadow: 'shadow-slate-200/50',
    roles: ['ADMIN', 'STAFF'],
  },
];

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

export default function AdminDashboardPage() {
  const { hasRole } = useAuth();
  const visibleCards = cards.filter((card) => card.roles.some((role) => hasRole(role)));

  return (
    <div className="space-y-8">
      <div className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400">Resumen</p>
        <h2 className="mt-3 text-[52px] leading-[0.96] font-semibold tracking-[-0.06em] text-slate-950">Panel de gestión</h2>
        <p className="text-slate-500 mt-4 max-w-3xl text-lg">
          Desde aquí puedes gestionar clientes, bonos y canjes. Selecciona una de las opciones o usa el menú lateral.
        </p>
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="grid gap-5 sm:grid-cols-2"
      >
        {visibleCards.map((card) => (
          <motion.div key={card.to} variants={cardVariant}>
            <Link
              to={card.to}
              className={`group block rounded-[28px] border border-[var(--app-border)] bg-[var(--app-surface)] p-6 shadow-lg ${card.shadow} backdrop-blur-xl transition hover:shadow-xl hover:-translate-y-1`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${card.gradient} text-2xl shadow-md`}>
                  {card.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold text-slate-950 group-hover:text-[var(--app-accent)] transition">
                    {card.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">{card.description}</p>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
